import { Popup, PopupSettings } from 'components/common/popup/Popup';
import React, { useMemo } from 'react';
import useBundleTranslation from 'i18n';
import { ConfirmationData } from '../AssociateRule';
import { Box, Stack, Typography } from '@mui/material';
import StaticInfo from 'components/common/StaticInfo';

interface IAssociateConfirmationPopupProps {
    showConfirm: boolean;
    data: ConfirmationData;
    onHide: () => void;
    onConfirm: () => void;
}

export default function AssociateConfirmationPopup({
    showConfirm,
    onHide,
    onConfirm,
    data,
}: IAssociateConfirmationPopupProps) {
    const { t } = useBundleTranslation(['app/editor/element/visualization-for-alert-rule']);

    const confirmPopupSettings: PopupSettings = {
        title: t('stage_two.popup_title'),
        textOK: t('stage_two.popup_ok_button'),
    };

    const metricsIds = useMemo(() => Object.keys(data.alerts), [data]);

    return (
        <Popup
            settings={confirmPopupSettings}
            maxWidth="popupMd"
            onHide={onHide}
            open={showConfirm}
            onConfirm={onConfirm}
        >
            <Stack spacing={1.5}>
                <StaticInfo
                    label={t('stage_two.confirm', {
                        metric_to: data.name,
                        metric_from: Object.values(data.metrics).join(', '),
                    })}
                    type={'info'}
                    planeText
                />
                <Stack spacing={0.75}>
                    {metricsIds.map((metricId) => {
                        const userRules = data.alerts[metricId].user;
                        const globalRules = data.alerts[metricId].global;

                        if (userRules.length > 0 || globalRules.length > 0) {
                            return (
                                <>
                                    <Typography variant={'heading'} fontWeight={600}>
                                        {data.metrics[metricId]}
                                    </Typography>

                                    {userRules.length > 0 && (
                                        <Box>
                                            <Typography fontWeight={600}>{t('stage_two.kpi_rules')}</Typography>
                                            <ul>
                                                {userRules.map((userRule) => (
                                                    <li>
                                                        {`"${userRule.name}" `}
                                                        <Typography
                                                            color={'error.main'}
                                                            fontWeight={400}
                                                            display={'inline'}
                                                        >{`${t('stage_two.currently')} ${userRule.viz}`}</Typography>
                                                        {`, ${t('stage_two.will_send')} ${data.name}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}

                                    {globalRules.length > 0 && (
                                        <Box>
                                            <Typography fontWeight={600}>{t('stage_two.global_rules')}</Typography>
                                            <ul>
                                                {globalRules.map((globalRule) => (
                                                    <li>
                                                        {`"${globalRule.name}" `}
                                                        <Typography
                                                            color={'error.main'}
                                                            fontWeight={400}
                                                            display={'inline'}
                                                        >{`${t('stage_two.currently')} ${globalRule.viz}`}</Typography>
                                                        {`, ${t('stage_two.will_send')} ${data.name}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                </>
                            );
                        }

                        return null;
                    })}
                </Stack>
            </Stack>
        </Popup>
    );
}
